// Rigid sprite renderer shared by arms, weapons and equipment modules: paints a
// part (authored in its own local frame) in both passes at any scale, stamps
// the silhouette ink outline, and reports the pixel origin of the local (0,0).

import type Phaser from 'phaser';
import { Drawer, canvas, ctx2d, stampOutlined, SIL_INK } from './draw';
import type { MechPalette } from './styles';

export interface Bounds {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface Sprite {
  /** Painted, outlined sprite. */
  body: HTMLCanvasElement;
  /** White emissive mask (same size/origin), or null if the part has no emissive elements. */
  glow: HTMLCanvasElement | null;
  /** Pixel position of the local origin inside the canvas. */
  ox: number;
  oy: number;
  scale: number;
}

/** Outline + glow-bleed margin around authored bounds (logical px). */
const PAD = 3;

function hasAlpha(c: HTMLCanvasElement): boolean {
  const data = ctx2d(c).getImageData(0, 0, c.width, c.height).data;
  for (let i = 3; i < data.length; i += 4) if (data[i] > 8) return true;
  return false;
}

export function renderSprite(b: Bounds, pal: MechPalette, draw: (d: Drawer) => void, scale = 1, glowK = 1): Sprite {
  const w = Math.ceil((b.x1 - b.x0 + PAD * 2) * scale);
  const h = Math.ceil((b.y1 - b.y0 + PAD * 2) * scale);
  const ox = (PAD - b.x0) * scale;
  const oy = (PAD - b.y0) * scale;
  const pass = (mode: 'body' | 'glow'): HTMLCanvasElement => {
    const c = canvas(w, h);
    const g = ctx2d(c);
    g.setTransform(scale, 0, 0, scale, ox, oy);
    const d = new Drawer(g, mode, pal, scale);
    d.glowK = glowK;
    d.inkScale = scale > 1 ? 0.8 : 1;
    draw(d);
    g.setTransform(1, 0, 0, 1, 0, 0);
    return c;
  };
  const raw = pass('body');
  const body = canvas(w, h);
  stampOutlined(ctx2d(body), 0, 0, raw, canvas(w, h), SIL_INK * scale * (scale > 1 ? 0.8 : 1));
  const glow = pass('glow');
  return { body, glow: hasAlpha(glow) ? glow : null, ox, oy, scale };
}

/** Register a canvas as a texture unless the key already exists (loaded art wins). */
export function addCanvasTexture(scene: Phaser.Scene, key: string, c: HTMLCanvasElement): void {
  if (scene.textures.exists(key)) return;
  scene.textures.addCanvas(key, c);
}
